import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import EChartCommon from '@op/components/EChartCommon';
import useLanguage from '@op/hooks/useLanguage';
import { getPercentageInfo, mathFormat } from '@op/utils';
import { formatTimeToShow } from '@op/utils/time';
import { reviewsFormat } from '@op/utils/value';
import React, { useMemo } from 'react';
import { detailPageTips } from '../../utils';
import { chartIconRich } from '../../MentionsSentiment/MetricsChart/Charts/utils';
import { textShowFun } from '../../MentionsSentiment/MetricsChart/utils';

export default function MentionsPieCharts({
  data,
  filter,
  specialHeight,
  eventsDict,
  detailPage,
  defaultSelected
}: any) {
  const eChartsRef = React.useRef(null as any);
  const { formatMessage } = useLanguage();

  const options = useMemo(() => {
    const negative = data.select_non_recommend?.review || 0;
    const positive = data.select_recommend?.review || 0;

    const mentionsTotal = positive + negative;

    return {
      tooltip: {
        borderWidth: 0,
        borderRadius: 6,
        padding: 0,
        formatter: (params: any) =>
          formatChartParamsDate({
            textShowFun: textShowFun(formatMessage, { byType: 'by_rating' }),
            params,
            listTitleFunc: (item) => formatMessage({ id: item.name }),
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header: formatTimeToShow(filter),
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({ id: 'Steam Added Reviews' }),
                },
              ];
              const finalRecords = detailPageTips(records, formatMessage, detailPage);
              return [
                {
                  showHeader: true,
                  records: finalRecords,
                  columns,
                },
              ];
            },
            valueFormat: (value) => {
              const percent = getPercentageInfo(
                params?.value,
                mentionsTotal,
                2,
              );
              return `${isNaN(value) ? '-' : reviewsFormat(value)}(${percent})`;
            },
          }),
      },
      series: {
        name: 'Steam Added Reviews',
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: '#fff',
          borderWidth: 2,
        },
        emphasis: {
          focus: 'self',
          label: {
            opacity: 0.2,
          },
          labelLine: {
            lineStyle: {
              opacity: 0.2,
            },
          },
        },
        blur: {
          label: { opacity: 0.2 },
          itemStyle: {
            opacity: 0.2,
          },
          labelLine: {
            lineStyle: {
              opacity: 0.2,
            },
          },
        },
        label: {
          color: '#262832',
          formatter: (params: any) => {
            const percent = mentionsTotal
              ? getPercentageInfo(params?.value, mentionsTotal, 2)
              : `${params.percent.toFixed(2)}%`;
            return `{value| ${formatMessage({
              id: params.name,
            })}}: {a|${mathFormat(params.value)} (${percent})}\n{hr|}\n{a|}`;
          },
          rich: {
            a: {
              // color: '#999',
              lineHeight: 20, // 设置最后一行空数据高度，为了能让延长线与hr线对接起来
              align: 'center',
            },
            hr: {
              // 设置hr是为了让中间线能够自适应长度
              borderColor: 'auto', // hr的颜色为auto时候会主动显示颜色的
              width: '105%',
              borderWidth: 0.5,
              height: 0.5,
            },
            per: {
              // 用百分比数据来调整下数字位置，显的好看些。如果不设置，formatter最后一行的空数据就不需要
              padding: [4, 0],
            },
          },
        },
        labelLayout: (params: any) => {
          const isLeft =
            params.labelRect.x <
            eChartsRef?.current?.getEchartsInstance?.()?.getWidth() / 2;
          const points = params.labelLinePoints;
          // Update the end point.
          points[2][0] = isLeft
            ? params.labelRect.x
            : params.labelRect.x + params.labelRect.width;
          return {
            labelLinePoints: points,
          };
        },
        data: [
          { value: positive, name: 'Positive' },
          { value: negative, name: 'Negative' },
        ],
      },
      title: [
        {
          text: `{name|${mathFormat(mentionsTotal)}}`,
          top: '40%',
          left: 'center',
          textStyle: {
            rich: {
              name: {
                fontSize: 24,
                fontFamily: 'num-m',
                color: '#000000',
                padding: [10, 0],
              },
            },
          },
        },
      ],
      legend: {
        formatter: (name: string) => formatMessage({ id: name }),
        textStyle: { rich: chartIconRich },
        ...(defaultSelected ? { selected: defaultSelected } : {}),
      },
    };
  }, [data, filter, formatMessage, defaultSelected]);

  return (
    <EChartCommon
      optionsProps={{ type: 'pie' }}
      className={`dbo-gamestore-chart-legend ${specialHeight === 0 ? 'dbo-gamestore-mentions-chart' : ''}`}
      height={245 + specialHeight}
      options={options}
      hasEmptyImg={false}
      ref={eChartsRef}
      eventsDict={eventsDict}
    ></EChartCommon>
  );
}
